/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, Optional} from '@angular/core';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import * as _ from 'lodash';
import {map, Observable, throwError} from 'rxjs';

import {ResourceHelper} from '../../components/util/resource-helper';
import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {DataViewApiService} from '../common/controller/data-view-api.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';
import {updateEndDateForManualInput} from './order-api-transforms';

export {updateEndDateForManualInput} from './order-api-transforms';

export interface OrderModel extends SignalAdminResourceModel {
  [key: string]: any;
}

@Injectable({providedIn: 'root'})
export class OrderApiService extends SignalAdminResourceApiBase<OrderModel> {
  constructor(client: AdnApiClientService, private readonly dataView: DataViewApiService, @Optional() localUserProfile?: LocalUserProfile) {
    super(client, 'orders', {showBudgetSpend: true}, () => localUserProfile?.isReadOnly() || false);
  }

  override save(model: OrderModel): Observable<OrderModel> {
    const payload = _.cloneDeep(model);
    updateEndDateForManualInput(payload, 'objectiveEndDate');
    ResourceHelper.checkObjectives(payload);
    delete payload.reportExecutions;
    return super.save(payload);
  }

  listOptions(params: Record<string, any> = {}): Observable<SignalAdminResourcePage<OrderModel>> {
    return this.client.request<SignalAdminResourcePage<OrderModel>>({
      method: 'GET',
      url: this.resourceUri('orders'),
      params: {showBudgetSpend: true, ...params}
    });
  }

  bulkSave(models: Partial<OrderModel>[]): Observable<void> {
    return this.saveMany(models);
  }

  copyOrder(
    order: OrderModel,
    newId: string,
    lineItemUserState: string,
    copyLineItems: boolean,
    copyTargeting: boolean,
    copyCreatives: boolean,
    copyBudgets: boolean,
    copyReportSchedules: boolean
  ): Observable<void> {
    if (!order?.id) {
      return throwError(() => 'Insufficient parameters for the order copy');
    }
    const copyOptions = {
      orderId: newId || '',
      advertiserId: _.get(order, ['advertiser', 'id'], ''),
      name: order.name,
      copyLineItems: _.isBoolean(copyLineItems) ? copyLineItems : true,
      copyTargeting: _.isBoolean(copyTargeting) ? copyTargeting : true,
      copyCreatives: _.isBoolean(copyCreatives) ? copyCreatives : true,
      copyReportSchedules: _.isBoolean(copyReportSchedules) ? copyReportSchedules : true,
      clearAdvertiser: true,
      clearBudgets: _.isBoolean(copyBudgets) ? !copyBudgets : false,
      lineItemUserState: lineItemUserState || ''
    };
    return this.client.request<void>({
      method: 'POST',
      url: this.resourceUri(['orders', order.id, 'copy']),
      params: {showBudgetSpend: true, ...copyOptions},
      body: {id: order.id, ...copyOptions}
    });
  }

  getListView(queryParams: Record<string, any>): Observable<any> {
    return this.dataView.listView('OrderListView', queryParams, undefined);
  }

  getDataView(params: {id: string}): Observable<OrderModel> {
    return this.dataView.getView('OrderView', params, undefined).pipe(
      map((dataView: any) => ({...dataView.order, advertiser: dataView.advertiser}))
    );
  }

  refresh(id: string): Observable<OrderModel> {
    return this.client.request<OrderModel>({
      method: 'POST',
      url: this.resourceUri(['orders', id]),
      params: {showBudgetSpend: true},
      body: {id}
    });
  }
}
